import { useCallback, useState, useEffect, useContext, useMemo } from 'react'
import { Form } from '@panwds/react-form'
import { pick, omit } from 'lodash'
import { validate as isValidUUID } from 'uuid'

import type { ComponentDef, ManifestFlags } from '../../renderer'
import { authStateRef, type CommonStoreState } from '../../store'
import ActivationBanner from '../common/ActivationBanner'
import {
  APP_ID_LOGGING_SERVICE,
  APP_ID_DSS_ID,
  VIEW_MANAGE_TENANT,
  ALLOCATION_FULL,
  ALLOCATION_SHARED,
  APP_ID_PRISMA_ACCESS_EDITION, APP_ID_FEDRAMP,
  APP_ID_IOT
} from '../common/GlobalConstants'
import { ACTIVATION_TYPE, TSG_NONE } from '../../../common/constants/GlobalConstants'
import TenancyService from '../../services/TenancyService'
import SubscriptionService from '../../services/SubscriptionService'
import { switchTsg, history, getAuthState, getFrameworkVar, setCurrentViewing, setSelectedTSG } from '../../../common/utils/Sparky'
import { ACTIVATION_UI_ERROR_CODES, ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP } from '../../ErrorCodes'
import ScreenLoader from '../common/ScreenLoader'
import { appIdsEligibleForActivation, isNonemptyArray, pendoEventTracker } from '../common/utils'
import { getSignature } from '../../../common/utils/Utils'
import { LoadTreeDataAsync } from '../../../common/actions/GlobalActions'
import GlobalContext from '../../../common/context/GlobalContext'
import MessageBannerV1 from '../common/MessageBannerV1'

import type { VFC } from 'react'
import type { Tsg } from '@sparky/framework'

type DefaultActivationFormProps = {
  children: ComponentDef
  state: CommonStoreState
  primaryButton: {
    text: string
  }
  readActivationInstances?: boolean
}

const UDefaultActivationForm: VFC<DefaultActivationFormProps> = ({ children, state, primaryButton, readActivationInstances }) => {
  const { licenses, hash, apps, tsg, instances, isHashFullyActivated, manifest, activationData } = state
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [infoMsg, setInfoMsg] = useState<string>('')
  const [activationInProgress, setActivationInProgress] = useState(false)
  const isGuid = isValidUUID(hash || '')
  const { current } = authStateRef

  const { dispatch } = useContext(GlobalContext)

  const appIdsToBeActivated = useMemo(() => {
    return appIdsEligibleForActivation(licenses, manifest?.primary_app_id as string, manifest?.flags as ManifestFlags)
  }, [licenses, manifest])

  useEffect(() => {
    if (isHashFullyActivated) {
      setInfoMsg(ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP.HASH_FULLY_ACTIVATED)
    }
    else {
      setInfoMsg('')
    }
  }, [licenses, tsg, setInfoMsg, instances, isHashFullyActivated, appIdsToBeActivated])

  const calculateNewTsg = useCallback((currentTsgName: string, tsgName: string, tsgDropDownDetails: any, count = 0): string => {
    const isNewTsgName = tsgDropDownDetails.some((each: any) => {
      return each.display_name === tsgName
    })
    if (!isNewTsgName) {
      return tsgName
    }
    else {
      count++
      return calculateNewTsg(currentTsgName, `${currentTsgName} - ${count}`, tsgDropDownDetails, count)
    }
  }, [])


  const getInstanceObject = useCallback((elem: any, support_account: any, props: any) => {
    const isAmend = (apps.get(elem.app_id)?.device_association?.license || manifest?.flags?.amend_license) && instances?.find((each) => each.app_id === elem.app_id)
    const response: any = {
      'app_id': elem.app_id,
      'support_account_id': support_account?.id?.toString(),
      'support_account_name': support_account?.description,
      'activation_type': isAmend ? ACTIVATION_TYPE.AMEND : ACTIVATION_TYPE.NEW,
      ...(isGuid ? { 'license_id': hash } : {}), // include license_id only if hash is license id (which is UUID)
      ...(elem.app_id === APP_ID_IOT && manifest?.flags?.sub_domain_required && { subdomain: props.subdomain })
    }
    if (!apps.get(elem.app_id)?.device_association?.license && elem.app_id !== APP_ID_DSS_ID) {
      response.allocation = elem.licenses.map((each: any) => {
        return {
          ...pick(each, ['app_id', 'license_type']),
          allocation_size: each.purchased_size
        }
      })
    }
    return response
  }, [apps, hash, instances, isGuid, manifest?.flags?.amend_license, manifest?.flags?.sub_domain_required])
  const formatInputRequest = useCallback((props: any) => {
    type AllocationType = 'FULL' | 'SHARED'
    type InstancesRequest = {
      is_eula_accepted: boolean
      tsg_name: string
      hash?: string
      amend_hash?: string
      platform_region: string
      allocation_type: AllocationType
      platform: string
      xsig?: string
      instances: any[]
      migrate_csp_instance?: any
      description?: string
    }
    const { tsg_name, region, support_account, cdl, cie, appAddons, description } = props
    let allocationType: AllocationType = ALLOCATION_FULL

    let instances: any[] = []
    const inputRequest: InstancesRequest = {
      'is_eula_accepted': true,
      'tsg_name': tsg_name,
      ...(!isGuid ? { 'hash': hash } : {}), // include hash only if hash is not license id (which is UUID)
      'platform_region': region,
      'allocation_type': ALLOCATION_FULL,
      'platform': activationData?.managementMethod ?? 'cloud',
      description,
      instances
    }
    if (isNonemptyArray(activationData?.migrate_csp_instance?.tenant_ids)) {
      inputRequest.migrate_csp_instance = activationData?.migrate_csp_instance
    }
    if (activationData?.readInstances || (activationData && readActivationInstances)) {
      const { instances: activationInstances } = activationData
      instances = activationInstances ? Object.values(activationInstances).filter(Boolean).map((each: any) => {
        const supportAccountId = support_account?.id?.toString()
        const support_account_name = support_account?.description
        if (each.allocation_type === ALLOCATION_SHARED) {
          // if any of them is SHARED, whole allocation is SHARED
          allocationType = ALLOCATION_SHARED
        }
        if (inputRequest?.platform === 'onprem' && each.app_id === APP_ID_PRISMA_ACCESS_EDITION) {
          const signature = getSignature(supportAccountId, state.supportAccounts)
          const notonorafter = getAuthState()?.user?.notonorafter
          const xsig = `${supportAccountId} ${notonorafter} ${signature}`
          inputRequest.xsig = xsig
        }
        if (each.description) {
          inputRequest.description = each.description
        }
        return omit({ ...each, support_account_id: supportAccountId, support_account_name }, ['allocation_type', 'description'])
      }).filter(_each => _each.app_id && _each.app_id !== APP_ID_FEDRAMP) : []
      inputRequest.instances = instances
    }
    if (licenses?.some(each => each.app_id === APP_ID_PRISMA_ACCESS_EDITION && each.is_upgrade)) {
      // TODO: use generic solution here
      // if PAE upgrade, replace hash with amend_hash key
      const amend_hash = hash
      delete inputRequest.hash
      inputRequest.amend_hash = amend_hash
    }
    if (licenses) {
      licenses.forEach((each: any) => {
        if (instances.find(inst => inst.app_id === each.app_id)) {
          // already created by activationData, skip
          return
        }
        if (appIdsToBeActivated.includes(each.app_id) && !(each.app_id === APP_ID_LOGGING_SERVICE && cdl && cdl.description === 'existing instance')) {
          instances.push(getInstanceObject(each, support_account, props))
        }
        if (cie && cie?.value !== 'None' && !instances.find(inst => inst.app_id === APP_ID_DSS_ID)) {
          instances.push(getInstanceObject({ app_id: APP_ID_DSS_ID }, support_account, props))
        }
      })
    }
    else {
      appIdsToBeActivated?.forEach(app_id => {
        instances.push({
          app_id,
          support_account_id: support_account?.id?.toString(),
          support_account_name: support_account?.value,
          activation_type: 'NEW',
          ...(isGuid ? { 'license_id': hash } : {})// include license_id only if hash is license id (which is UUID)
        })
      })
    }
    if (appAddons?.length) { // add app addons to final instances list
      appAddons.forEach((each: { app_id: string, required_x_signature: boolean }) => {
        const support_account_id = support_account?.id
        const signature = state.supportAccounts?.find(e => e.accountid === support_account_id)?.signature
        const notonorafter = current?.user?.notonorafter
        const x_sig = `${support_account_id} ${notonorafter} ${signature}`
        instances.push({
          app_id: each.app_id,
          support_account_id: support_account_id?.toString(),
          ...(each.required_x_signature && { 'x-sig': x_sig }),
          activation_type: 'NEW',
        })
      })
    }
    inputRequest.allocation_type = allocationType
    return inputRequest
  }, [isGuid, hash, activationData, readActivationInstances, licenses, state.supportAccounts, appIdsToBeActivated, getInstanceObject, current?.user?.notonorafter])

  const onPrimaryBtnClick = useCallback(async (values: any) => {
    setActivationInProgress(true)
    const tsgData = {
      tsg_id: values.tsg?.id,
      tsg_name: values.tsg?.value,
      isError: false
    }
    // day zero case, user create new TSG
    if (activationData?.tsgCreateList && isNonemptyArray(activationData?.tsgCreateList)) {
      const createTsgs = async (tsgCreateList: { id?: string, display_name: string, parent_id?: string }[]) => {
        const _tsgCreateList = Array.from(tsgCreateList)
        let parent_id: string | undefined
        // iteratively create tsg tree
        for (const each of _tsgCreateList) {
          const payload = {
            display_name: each.display_name,
            parent_id,
          }
          const { data } = await TenancyService.createTenant.post(payload, parent_id ?? TSG_NONE.id).catch((err) => {
            setErrorMsg(ACTIVATION_UI_ERROR_CODES.CREATE_TENANT_FAILED)
            setActivationInProgress(false)
            tsgData.isError = true
            return { data: undefined }
          })
          each.id = data.id
          each.display_name = data.display_name
          parent_id = data.id
        }
        const recipient = _tsgCreateList[_tsgCreateList.length - 1]
        tsgData.tsg_id = recipient.id
        tsgData.tsg_name = recipient.display_name
      }
      const { tsgCreateList } = activationData
      await createTsgs(tsgCreateList)
    }
    if (tsgData.isError) {
      return
    }
    const request = [formatInputRequest({
      tsg_name: tsgData.tsg_name,
      region: values['region'].id,
      support_account: values['csp'],
      cdl: values['cdl'],
      cie: values['cie'],
      subdomain: values['subdomain'],
      appAddons: values['appAddons'],
      description: values['description']
    })]
    // setActivationInProgress(false)
    // return console.log('instance request', request)
    SubscriptionService.activateInstance.post(request, tsgData.tsg_id).then(async (response) => {
      await LoadTreeDataAsync(dispatch)
      await switchTsg(tsgData.tsg_id)
      setActivationInProgress(false)
      if (getFrameworkVar('hub')) {
        // if on hub, needs to go back to home page
        history.push('/hub')
      }
      if (getFrameworkVar('sase')) {
        if (state.context === 'cgx') {
          history.push('/sdwan')
        }
        else {
          history.push('/prisma_access')
        }
      }
      setCurrentViewing(VIEW_MANAGE_TENANT)
      setSelectedTSG({ id: tsgData.tsg_id, display_name: tsgData.tsg_name } as Tsg)
      pendoEventTracker({
        name: 'activation_success',
        request,
        status: 200,
        message: JSON.stringify(response)
      })
    }).catch((err) => {
      setActivationInProgress(false)
      setErrorMsg(err?.message || ACTIVATION_UI_ERROR_CODES.ACTIVATION_FAILED)

      pendoEventTracker({
        name: 'activation_error',
        request,
        status: err.statusCode,
        message: err?.message || ACTIVATION_UI_ERROR_CODES.ACTIVATION_FAILED
      })
    })

  }, [activationData, dispatch, formatInputRequest])

  const clearErrorMessage = useCallback(() => {
    setErrorMsg('')
  }, [])

  const groupedChildren = useMemo(() => {
    const subChildren = children?.props?.children
    const groupedChilderen = new Map()
    if (subChildren && Array.isArray(subChildren)) {
      subChildren.forEach(child => {
        const key = child?.props?.group || 1
        const collection = groupedChilderen.get(key) || []
        if (collection.length) {
          collection.push(child)
        }
        else {
          groupedChilderen.set(key, [child])
        }
      })
    }
    else {
      groupedChilderen.set(1, subChildren)
    }
    return groupedChilderen
  }, [children])

  return <div className='cm-w-[1000px]'>
    {activationInProgress && <ScreenLoader transparent={false} appearance='light' />}
    <div className='cm-fixed cm-z-50 cm-w-[1000px] cm-top-0'>
      {infoMsg && <ActivationBanner infoMessage={infoMsg} appearance='standard' type='page' />}
      {errorMsg && <MessageBannerV1 message={errorMsg} callBack={clearErrorMessage} donNotCloseBanner={true} />}
    </div>
    <div className='cm-flex cm-flex-row'>
      <Form onSubmit={onPrimaryBtnClick} mode='onBlur' formProps={{ style: { height: '100%', width: '100%' } }}>
        <div>
          {groupedChildren.get(1)}
        </div>
        {groupedChildren.has(2) && <div className='cm-mt-2'>
          {groupedChildren.get(2)}
        </div>}
        {groupedChildren.has(3) && <div className='cm-mt-3'>
          {groupedChildren.get(3)}
        </div>}
      </Form>
    </div>
  </div>
}
export default UDefaultActivationForm
